// Synthesis toolkit for the sound bank (bank.ts): voices drawn into any audio context, live or offline. Each voice
// has a click-free attack, a natural exponential decay, optional filter sweeps and stereo position. The building
// blocks of a sound designer's kit: FM bells, plucks, filtered noise, pitched thumps, metallic clangs, a soft saturator.

export interface Kit { ac: BaseAudioContext; out: AudioNode; noise: AudioBuffer }

interface Env { at: number; dur: number; vol: number; attack?: number; pan?: number }
export interface ToneOpts extends Env { f: number; to?: number; type?: OscillatorType; lp?: number; lpTo?: number; q?: number; detune?: number; glide?: number }
export interface FmOpts extends Env { f: number; ratio: number; index: number; to?: number; detune?: number }
export interface NoiseOpts extends Env { filter?: BiquadFilterType; f: number; to?: number; q?: number }

/** Voice output: a gain with an envelope, panned, into the kit's output. */
function voice(k: Kit, e: Env): GainNode {
  const g = k.ac.createGain(), t = e.at, a = Math.max(0.001, e.attack ?? 0.004);
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(e.vol, t + a);
  // exponential approach to silence: a time constant of a fifth of the length reaches -43 dB at its end
  g.gain.setTargetAtTime(0, t + a, Math.max(0.004, (e.dur - a) / 5));
  if (e.pan) { const p = k.ac.createStereoPanner(); p.pan.value = Math.max(-1, Math.min(1, e.pan)); g.connect(p).connect(k.out); } else g.connect(k.out);
  return g;
}

const filter = (k: Kit, type: BiquadFilterType, f: number, q: number) => { const x = k.ac.createBiquadFilter(); x.type = type; x.frequency.value = f; x.Q.value = q; return x; };

/** An oscillator, optionally detuned in two layers (warmth), with a pitch glide and a filter sweep. */
export function tone(k: Kit, o: ToneOpts) {
  const g = voice(k, o), t = o.at, end = t + o.dur, layers = o.detune ? [-o.detune, o.detune] : [0];
  let head: AudioNode = g;
  if (o.lp) {
    const f = filter(k, 'lowpass', o.lp, o.q ?? 0.7);
    if (o.lpTo) f.frequency.exponentialRampToValueAtTime(o.lpTo, end);
    f.connect(g); head = f;
  }
  for (const c of layers) {
    const osc = k.ac.createOscillator(), lg = k.ac.createGain();
    osc.type = o.type ?? 'triangle'; osc.detune.value = c; lg.gain.value = 1 / Math.sqrt(layers.length);
    osc.frequency.setValueAtTime(o.f, t);
    if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t + (o.glide ?? o.dur));
    osc.connect(lg).connect(head); osc.start(t); osc.stop(end + 0.05);
  }
}

/**
 * FM bell: a sine carrier modulated by another sine at `ratio` times its pitch, the modulation fading faster than
 * the note. Integer ratios ring like bells and chimes, irrational ones like struck metal.
 */
export function fm(k: Kit, o: FmOpts) {
  const g = voice(k, o), t = o.at, end = t + o.dur;
  for (const c of o.detune ? [-o.detune, o.detune] : [0]) {
    const car = k.ac.createOscillator(), mod = k.ac.createOscillator(), depth = k.ac.createGain(), lg = k.ac.createGain();
    car.type = 'sine'; mod.type = 'sine'; car.detune.value = c; mod.detune.value = c;
    car.frequency.setValueAtTime(o.f, t); mod.frequency.setValueAtTime(o.f * o.ratio, t);
    if (o.to) { car.frequency.exponentialRampToValueAtTime(o.to, end); mod.frequency.exponentialRampToValueAtTime(o.to * o.ratio, end); }
    depth.gain.setValueAtTime(o.f * o.index, t);
    depth.gain.setTargetAtTime(o.f * o.index * 0.08, t + 0.005, o.dur / 7);
    lg.gain.value = o.detune ? 0.7 : 1;
    mod.connect(depth).connect(car.frequency); car.connect(lg).connect(g);
    car.start(t); mod.start(t); car.stop(end + 0.05); mod.stop(end + 0.05);
  }
}

/** Filtered noise with a cutoff sweep: air, whooshes, impacts, crowds. */
export function noise(k: Kit, o: NoiseOpts) {
  const g = voice(k, o), t = o.at, src = k.ac.createBufferSource(), f = filter(k, o.filter ?? 'lowpass', o.f, o.q ?? 0.8);
  src.buffer = k.noise; src.loop = true;
  if (o.to) f.frequency.exponentialRampToValueAtTime(o.to, t + o.dur);
  src.connect(f).connect(g);
  src.start(t, Math.random() * 1.5); src.stop(t + o.dur + 0.05);
}

/** A pitched thump: the body of an impact, a kick drum's sine dropping fast in pitch. */
export function thump(k: Kit, at: number, f: number, to: number, dur: number, vol: number, pan = 0) {
  tone(k, { at, dur, vol, pan, f, to, type: 'sine', glide: dur * 0.5, attack: 0.002 });
}

/** A transient: a few milliseconds of bright noise that gives a sound its edge. */
export function click(k: Kit, at: number, f: number, vol: number, pan = 0) {
  noise(k, { at, dur: 0.012, vol, pan, filter: 'bandpass', f, q: 1.2, attack: 0.0008 });
}

/** Struck metal: inharmonic partials decaying at different rates (a clang, a cowbell, a bike frame). */
export function metal(k: Kit, at: number, f: number, dur: number, vol: number, pan = 0) {
  const parts = [[1, 1], [2.76, 0.5], [5.4, 0.28], [8.93, 0.14]];
  for (const [r, a] of parts) tone(k, { at, dur: dur / Math.sqrt(r), vol: vol * a, f: f * r, type: 'sine', pan, attack: 0.001 });
}

/** A soft saturator in front of the output: drives impacts into a warm, dense crunch instead of a thin click. */
export function saturate(k: Kit, drive: number): Kit {
  const ws = k.ac.createWaveShaper(), n = 1024, curve = new Float32Array(n);
  for (let i = 0; i < n; i++) { const x = (i / (n - 1)) * 2 - 1; curve[i] = Math.tanh(x * drive) / Math.tanh(drive); }
  ws.curve = curve; ws.oversample = '2x';
  ws.connect(k.out);
  return { ...k, out: ws };
}

/** Notes by name (equal temperament, A4 = 440 Hz), so the stings share one musical key. */
export function note(name: string): number {
  const m = /^([A-G])(#|b)?(\d)$/.exec(name);
  if (!m) throw new Error(`bad note ${name}`);
  const semis: Record<string, number> = { C: -9, D: -7, E: -5, F: -4, G: -2, A: 0, B: 2 };
  const n = semis[m[1]] + (m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0) + (Number(m[3]) - 4) * 12;
  return 440 * Math.pow(2, n / 12);
}
